import { MAX_REPEAT_COUNT } from '@trinity/management-app-client/app-v1/form-schemas';
import type {
  FieldDefinition,
  FormFieldValue,
  FormSchema,
  renderDynamicField,
  SectionDefinition,
} from './DynamicForm.form.fields';

export type { FieldDefinition, FormFieldValue, FormSchema, SectionDefinition };

// Indexed-name convention: instance 1 keeps the bare name, instance k>=2 suffixes `_${k}` (e.g. "category_2").
function indexedFieldName(baseName: string, instanceIndex: number): string {
  return instanceIndex === 0 ? baseName : `${baseName}_${instanceIndex + 1}`;
}

// Expands a repeatable form's one template section/fields into MAX_REPEAT_COUNT copies, each with a unique sectionId and indexed field names.
// All fields after the first instance are marked as not required, so that the user can submit a partially filled repeatable form.
export function expandRepeatableSchema(formConfig: FormSchema): FormSchema {
  // no op if the form is not repeatable
  if (!formConfig.repeatable) {
    return formConfig;
  }

  const templateSection = formConfig.sections[0];

  if (!templateSection) {
    throw new Error('expandRepeatableSchema: repeatable form has no template section');
  }

  const templateFields = [...formConfig.fields].sort((a, b) => a.order - b.order);

  const sections: SectionDefinition[] = [];
  const fields: FieldDefinition[] = [];

  for (let instanceIndex = 0; instanceIndex < MAX_REPEAT_COUNT; instanceIndex++) {
    const sectionId = `${templateSection.id}_${instanceIndex + 1}`;

    sections.push({
      ...templateSection,
      id: sectionId,
      order: instanceIndex,
    });

    templateFields.forEach((field, fieldPosition) => {
      fields.push({
        ...field,
        id: `${field.id}_${instanceIndex + 1}`,
        name: indexedFieldName(field.name, instanceIndex),
        sectionId,
        order: instanceIndex * templateFields.length + fieldPosition,
        required: instanceIndex === 0 ? field.required : false,
      });
    });
  }

  return {
    ...formConfig,
    fields,
    sections,
  };
}

export type RepeatableBlock = Record<string, FormFieldValue>;

type RenderFieldParams = Parameters<typeof renderDynamicField>;
export type RepeatableAIContext = {
  aiState: RenderFieldParams[5];
  onAICorrection: RenderFieldParams[6];
  correctionsApplied: RenderFieldParams[7];
  animationPhase: RenderFieldParams[8];
};

export type RepeatableTemplate = {
  sectionTitle: string;
  baseFields: FieldDefinition[];
  allFields: FieldDefinition[];
};

export function deriveRepeatableTemplate(formConfig: FormSchema): RepeatableTemplate | null {
  if (!formConfig.repeatable) {
    return null;
  }

  const sortedSections = [...(formConfig.sections ?? [])].sort((a, b) => a.order - b.order);
  const firstSection = sortedSections[0];
  if (!firstSection) {
    return null;
  }

  // Hidden system fields (visibleToUser: false) are excluded from display, matching renderDynamicField/renderSubmittedField elsewhere in this folder. They're never populated by this editing flow, so formDataUpdatesFromBlocks intentionally doesn't track them for shrink-clearing either.
  const baseFields = formConfig.fields
    .filter((field) => field.sectionId === firstSection.id)
    .filter((field) => !((field.type === 'static' || field.type === 'variable') && field.visibleToUser === false))
    .sort((a, b) => a.order - b.order);

  if (baseFields.length === 0) {
    return null;
  }

  return {
    sectionTitle: firstSection.title,
    baseFields,
    allFields: formConfig.fields,
  };
}

export function fieldsForInstance(template: RepeatableTemplate, instanceIndex: number): FieldDefinition[] {
  return template.baseFields.map((base) => {
    const indexedName = indexedFieldName(base.name, instanceIndex);
    const field = template.allFields.find((f) => f.name === indexedName);
    if (!field) {
      throw new Error(`fieldsForInstance: no expanded field found for "${indexedName}"`);
    }
    return field;
  });
}

function isMeaningfulValue(value: FormFieldValue): boolean {
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  return typeof value === 'string' && value.trim().length > 0;
}

function toFormFieldValue(value: unknown): FormFieldValue {
  if (typeof value === 'string') {
    return value;
  }
  if (Array.isArray(value) && value.every((item) => typeof item === 'string')) {
    return value;
  }
  return undefined;
}

export function createEmptyBlock(template: RepeatableTemplate): RepeatableBlock {
  const block: RepeatableBlock = {};
  template.baseFields.forEach((field) => {
    const optionDefault =
      field.type === 'radio' || field.type === 'select'
        ? field.options?.find((opt) => opt.defaultSelected)?.value
        : undefined;
    const defaultValue = typeof field.defaultValue === 'string' ? field.defaultValue : undefined;
    block[field.name] = defaultValue ?? optionDefault ?? '';
  });
  return block;
}

export function blocksFromFormData(
  template: RepeatableTemplate,
  formData: Record<string, unknown>
): RepeatableBlock[] {
  const blocks: RepeatableBlock[] = [];

  for (let i = 0; i < MAX_REPEAT_COUNT; i++) {
    const block: RepeatableBlock = {};
    let hasValue = false;

    template.baseFields.forEach((field) => {
      const value = toFormFieldValue(formData[indexedFieldName(field.name, i)]);
      block[field.name] = value;
      if (isMeaningfulValue(value)) {
        hasValue = true;
      }
    });

    if (hasValue) {
      blocks.push(block);
    }
  }

  return blocks.length > 0 ? blocks : [createEmptyBlock(template)];
}

export function formDataUpdatesFromBlocks(
  template: RepeatableTemplate,
  blocks: RepeatableBlock[]
): Record<string, FormFieldValue> {
  const updates: Record<string, FormFieldValue> = {};

  for (let i = 0; i < MAX_REPEAT_COUNT; i++) {
    const block = blocks[i];
    template.baseFields.forEach((field) => {
      updates[indexedFieldName(field.name, i)] = block ? (block[field.name] ?? '') : '';
    });
  }

  return updates;
}
